import { createContext, useContext, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import API from '@/lib/api';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load organizations for superadmin
  useEffect(() => {
    if (user?.role === 'superadmin') {
      loadOrganizations();
    } else {
      // Non-superadmin: clear organizations
      setOrganizations([]);
      setSelectedOrg(null);
    }
  }, [user]);

  // Invalidate all queries when organization changes (force refetch)
  useEffect(() => {
    if (selectedOrg?.id && user?.role === 'superadmin') {
      // Invalidate all queries to force refetch with new organization
      queryClient.invalidateQueries();
    }
  }, [selectedOrg?.id, user?.role, queryClient]);

  // Sync with URL params (bidirectional)
  useEffect(() => {
    const orgId = searchParams.get('org');

    if (user?.role !== 'superadmin') {
      return; // Non-superadmin doesn't use URL org param
    }

    if (orgId && organizations.length > 0) {
      // URL has org param - sync state with it
      const org = organizations.find(o => o.id === orgId);
      if (org && org.id !== selectedOrg?.id) {
        setSelectedOrg(org);
      }
    } else if (selectedOrg?.id && organizations.length > 0) {
      // State has org but URL doesn't - restore URL param
      setSearchParams({ org: selectedOrg.id }, { replace: true });
    }
  }, [searchParams, organizations, selectedOrg?.id, user?.role, setSearchParams]);

  const loadOrganizations = async () => {
    setLoading(true);
    try {
      const res = await API.get('/superadmin/organizations/simple');
      const orgs = res.data;
      setOrganizations(orgs);

      // Auto-select first org alphabetically if none selected
      const orgIdFromUrl = searchParams.get('org');
      if (orgIdFromUrl && orgs.length > 0) {
        // Try to restore from URL
        const orgFromUrl = orgs.find(o => o.id === orgIdFromUrl);
        if (orgFromUrl) {
          setSelectedOrg(orgFromUrl);
        } else {
          // Invalid org in URL, select first
          selectFirstOrg(orgs);
        }
      } else if (!selectedOrg && orgs.length > 0) {
        // No org in URL, auto-select first
        selectFirstOrg(orgs);
      }
    } catch (err) {
      console.error('Failed to load organizations:', err);
      setOrganizations([]);
    }
    setLoading(false);
  };

  const selectFirstOrg = (orgs) => {
    // Organizations are already sorted alphabetically from backend
    const firstOrg = orgs[0];
    setSelectedOrg(firstOrg);
    setSearchParams({ org: firstOrg.id }, { replace: true });
  };

  const selectOrganization = (org) => {
    setSelectedOrg(org);
    if (org) {
      setSearchParams({ org: org.id });
    } else {
      setSearchParams({});
    }
  };

  const value = {
    organizations,
    selectedOrg,
    setSelectedOrg: selectOrganization,
    loading,
    isSuperadmin: user?.role === 'superadmin'
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganizationContext must be used within OrganizationProvider');
  }
  return context;
};
